import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MaintenanceMessage from './MaintenanceMessage';
import MaintenanceUpcomingNotice from './MaintenanceUpcomingNotice';
import useMaintenanceUpcomingWarnings from '../hooks/useMaintenanceUpcomingWarnings';
import { useAuth } from '../contexts/AuthContext';
import {
  clearMaintenanceBlock,
  isClientMaintainer,
  notifyMaintenanceBlock,
  setClientMaintainerFlag,
  setMaintainerMaintenanceBypass,
  setMaintenanceBlockListener,
} from '../utils/maintenanceBlock';

const MAINTENANCE_POLL_MS = 60000;

const MaintenanceBlockHost = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [maintenance, setMaintenance] = useState(null);
  const isMaintainer = isClientMaintainer() || user?.maintainer === true;
  const canPollMaintenance = !authLoading;

  const {
    activeWarning,
    noticeOpen,
    handleNoticeClose,
  } = useMaintenanceUpcomingWarnings({
    enabled: isAuthenticated,
    tokenKey: 'token',
  });

  const applyMaintenance = useCallback((next) => {
    if (next?.maintainer === true) {
      setClientMaintainerFlag(true);
    }
    if (isClientMaintainer() || next?.maintainer === true) {
      clearMaintenanceBlock();
      return;
    }
    if (next?.active) {
      notifyMaintenanceBlock(next);
      return;
    }
    clearMaintenanceBlock();
  }, []);

  useEffect(() => {
    setMaintenanceBlockListener(setMaintenance);
    return () => setMaintenanceBlockListener(null);
  }, []);

  useEffect(() => {
    setMaintainerMaintenanceBypass(isMaintainer);
    if (isMaintainer) {
      clearMaintenanceBlock();
    }
  }, [isMaintainer]);

  useEffect(() => {
    if (!canPollMaintenance) return undefined;

    if (isMaintainer) {
      clearMaintenanceBlock();
      return undefined;
    }

    let cancelled = false;

    const pollCurrentMaintenance = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const { data } = await axios.get('/api/maintenance/now', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          applyMaintenance(data);
        }
      } catch (_e) {
        // 503 maintenance payloads are handled by the global axios interceptor.
      }
    };

    pollCurrentMaintenance();
    const intervalId = window.setInterval(pollCurrentMaintenance, MAINTENANCE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [applyMaintenance, isMaintainer, canPollMaintenance]);

  return (
    <>
      <MaintenanceUpcomingNotice
        open={noticeOpen}
        warning={activeWarning}
        onClose={handleNoticeClose}
      />
      <MaintenanceMessage
        open={Boolean(maintenance) && !isMaintainer}
        maintenance={maintenance}
      />
    </>
  );
};

export default MaintenanceBlockHost;
